import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useSeo } from '../../hooks/useSeo';
import TeamSection from './TeamSection';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { PHOTO_CREDITS, PHOTO_CREDITS_ANCHOR } from '../../config/photoCredits';
import { BrandRule } from '../../components/brand/BrandRule';

// Inline text link on the credits list: brand teal, a quiet underline that
// strengthens on hover, and the site's one focus ring.
const CREDIT_LINK =
  'rounded-sm font-medium text-[var(--pv-color-primary)] underline decoration-[var(--pv-border-strong)] underline-offset-2 transition-colors hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)] focus-visible:ring-offset-1';

// Every section: white, a hairline above it, the same heading (THEME.md §3).
const SECTION = 'border-t border-[var(--pv-border-subtle)] py-14';
const H2 = 'text-[1.5rem] font-bold text-[var(--pv-color-primary)] sm:text-[1.75rem]';
const H3 = 'flex items-center gap-2 text-[1.0625rem] font-semibold text-[var(--pv-color-text)]';

type ValueItem = {
  icon: string;
  title: string;
  description: string;
};

type FeatureItem = {
  icon: string;
  title: string;
  description: string;
};

type StatItem = {
  value: string;
  label: string;
};

export default function AboutPage() {
  const { t, i18n } = useTranslation('about');
  const isRTL = i18n.dir() === 'rtl';
  useSeo({
    title: isRTL ? 'من نحن | صفقات 24' : 'About us | Safaqat 24',
    description: isRTL
      ? 'تعرّف على صفقات 24، منصة عقارية سعودية للبيع والإيجار.'
      : 'About Safaqat 24, a Saudi real-estate marketplace for sale and rent.',
  });
  const textAlignClass = isRTL ? 'text-right' : 'text-left';
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  // In-page anchors (the footer's «حقوق الصور» → /about#photo-credits). React
  // Router does not scroll to a hash, and the router's own scroll-to-top for a
  // new route runs in an effect AFTER this page's (parents run last), so the
  // jump waits one frame. Focus follows the view for keyboard and
  // screen-reader users. `location.key` re-runs it when the same link is used
  // again from this page.
  useEffect(() => {
    let id = '';
    try {
      id = decodeURIComponent(location.hash.replace(/^#/, ''));
    } catch {
      return undefined;
    }
    if (!id) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      const focusTarget = target.querySelector<HTMLElement>('[data-anchor-focus]') ?? target;
      focusTarget.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, location.key, reduceMotion]);

  const hero = useMemo(() => {
    const raw = t('about.hero', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      return raw as { badge?: string; title?: string; subtitle?: string };
    }
    return {};
  }, [t, i18n.language]);

  const mission = useMemo(() => {
    const raw = t('about.mission', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      return raw as { title?: string; description?: string };
    }
    return {};
  }, [t, i18n.language]);

  const vision = useMemo(() => {
    const raw = t('about.vision', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      return raw as { title?: string; description?: string };
    }
    return {};
  }, [t, i18n.language]);

  const story = useMemo(() => {
    const raw = t('about.story', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      const data = raw as { title?: string; paragraphs?: string[] };
      return {
        title: data.title ?? '',
        paragraphs: Array.isArray(data.paragraphs) ? data.paragraphs : [],
      };
    }
    return { title: '', paragraphs: [] };
  }, [t, i18n.language]);

  const values = useMemo(() => {
    const raw = t('about.values', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      const data = raw as { title?: string; items?: ValueItem[] };
      return {
        title: data.title ?? '',
        items: Array.isArray(data.items) ? data.items : [],
      };
    }
    return { title: '', items: [] };
  }, [t, i18n.language]);

  const platform = useMemo(() => {
    const raw = t('about.platform', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      const data = raw as { title?: string; subtitle?: string; features?: FeatureItem[] };
      return {
        title: data.title ?? '',
        subtitle: data.subtitle ?? '',
        features: Array.isArray(data.features) ? data.features : [],
      };
    }
    return { title: '', subtitle: '', features: [] };
  }, [t, i18n.language]);

  const compliance = useMemo(() => {
    const raw = t('about.compliance', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      const data = raw as { title?: string; description?: string; items?: string[] };
      return {
        title: data.title ?? '',
        description: data.description ?? '',
        items: Array.isArray(data.items) ? data.items : [],
      };
    }
    return { title: '', description: '', items: [] };
  }, [t, i18n.language]);

  const stats = useMemo(() => {
    const raw = t('about.stats', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      const data = raw as { title?: string; items?: StatItem[] };
      return {
        title: data.title ?? '',
        items: Array.isArray(data.items) ? data.items : [],
      };
    }
    return { title: '', items: [] };
  }, [t, i18n.language]);

  const cta = useMemo(() => {
    const raw = t('about.cta', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      return raw as { title?: string; description?: string; primaryButton?: string; secondaryButton?: string };
    }
    return {};
  }, [t, i18n.language]);

  return (
    <div
      className="min-h-screen bg-[var(--pv-color-surface)] text-[var(--pv-color-text-muted)]"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navigation />

      {/* One calm page (owner, 2026-10-07): white sections split by hairlines,
          the copy as it was, no eyebrow pill, no icon tile per item and no box
          per paragraph. */}
      <header className="mx-auto max-w-4xl px-6 pb-14 pt-14 text-center sm:pt-20">
        <h1 className="text-[2rem] font-bold leading-tight text-[var(--pv-color-primary)] sm:text-[2.75rem]">{hero.title}</h1>
        <BrandRule className="mx-auto mt-4" />
        <p className="mx-auto mt-4 max-w-2xl text-[1.0625rem] leading-relaxed sm:text-[1.125rem]">{hero.subtitle}</p>
      </header>

      <main>
        {/* Mission & vision, side by side */}
        <section className={SECTION}>
          <div className="mx-auto grid max-w-5xl gap-10 px-6 md:grid-cols-2">
            {[
              { icon: 'ri-compass-3-line', block: mission },
              { icon: 'ri-eye-line', block: vision },
            ].map(({ icon, block }) => (
              <div key={icon} className={textAlignClass}>
                <h2 className={`${H2} flex items-center gap-2.5`}>
                  <i className={`${icon} text-[1.5rem] leading-none`} aria-hidden="true" />
                  {block.title}
                </h2>
                <p className="mt-3 text-[1rem] leading-relaxed">{block.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Our story */}
        {story.paragraphs.length > 0 && (
          <section className={SECTION}>
            <div className={`mx-auto max-w-3xl px-6 ${textAlignClass}`}>
              <h2 className={H2}>{story.title}</h2>
              <div className="mt-4 space-y-4">
                {story.paragraphs.map((paragraph, idx) => (
                  <p key={idx} className="text-[1rem] leading-relaxed sm:text-[1.0625rem]">{paragraph}</p>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* The short facts, as one row */}
        {stats.items.length > 0 && (
          <section className={SECTION} aria-labelledby="about-stats-title">
            <div className="mx-auto max-w-5xl px-6">
              <h2 id="about-stats-title" className="sr-only">{stats.title}</h2>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-8 text-center md:grid-cols-4">
                {stats.items.map((stat, idx) => (
                  <div key={idx} className="flex flex-col-reverse">
                    <dt className="mt-1 text-[0.9375rem]">{stat.label}</dt>
                    <dd className="m-0 text-[1.75rem] font-bold text-[var(--pv-color-primary)] sm:text-[2rem]">{stat.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        )}

        {/* Values */}
        <section className={SECTION}>
          <div className="mx-auto max-w-5xl px-6">
            <h2 className={`${H2} ${textAlignClass}`}>{values.title}</h2>
            <ul role="list" className="m-0 mt-6 grid list-none gap-x-10 gap-y-7 p-0 sm:grid-cols-2 lg:grid-cols-3">
              {values.items.map((value, idx) => (
                <li key={idx} className={textAlignClass}>
                  <h3 className={H3}>
                    <i className={`${value.icon} text-[1.25rem] leading-none text-[var(--pv-color-primary)]`} aria-hidden="true" />
                    {value.title}
                  </h3>
                  <p className="mt-2 text-[0.9375rem] leading-relaxed">{value.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* What we offer */}
        <section className={SECTION}>
          <div className="mx-auto max-w-5xl px-6">
            <h2 className={`${H2} ${textAlignClass}`}>{platform.title}</h2>
            {platform.subtitle && <p className={`mt-2 text-[1rem] ${textAlignClass}`}>{platform.subtitle}</p>}
            <ul role="list" className="m-0 mt-6 grid list-none gap-x-10 gap-y-7 p-0 sm:grid-cols-2 lg:grid-cols-4">
              {platform.features.map((feature, idx) => (
                <li key={idx} className={textAlignClass}>
                  <h3 className={H3}>
                    <i className={`${feature.icon} text-[1.25rem] leading-none text-[var(--pv-color-primary)]`} aria-hidden="true" />
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-[0.9375rem] leading-relaxed">{feature.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Regulated and compliant */}
        <section className={SECTION}>
          <div className={`mx-auto max-w-5xl px-6 ${textAlignClass}`}>
            <h2 className={`${H2} flex items-center gap-2.5`}>
              <i className="ri-government-line text-[1.5rem] leading-none" aria-hidden="true" />
              {compliance.title}
            </h2>
            <p className="mt-3 max-w-3xl text-[1rem] leading-relaxed">{compliance.description}</p>
            <ul role="list" className="m-0 mt-5 grid list-none gap-x-10 gap-y-3 p-0 sm:grid-cols-2">
              {compliance.items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2.5 text-[0.9375rem] text-[var(--pv-color-text)]">
                  <i className="ri-check-line mt-0.5 shrink-0 text-[1.125rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <TeamSection />

        {/* CTA */}
        <section className={`${SECTION} text-center`}>
          <div className="mx-auto max-w-3xl px-6">
            <h2 className={H2}>{cta.title}</h2>
            <p className="mx-auto mt-3 max-w-xl text-[1rem]">{cta.description}</p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/search"
                className="inline-flex h-12 items-center gap-2 rounded-full bg-[var(--pv-color-primary)] px-7 text-[0.9375rem] font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)] focus-visible:ring-offset-2 dark:text-[var(--pv-color-surface)]"
              >
                <i className="ri-search-line" aria-hidden="true" />
                {cta.primaryButton}
              </Link>
              <Link
                to="/contact"
                className="inline-flex h-12 items-center gap-2 rounded-full border border-[var(--pv-border-strong)] px-7 text-[0.9375rem] font-semibold text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)] focus-visible:ring-offset-2"
              >
                <i className="ri-mail-line" aria-hidden="true" />
                {cta.secondaryButton}
              </Link>
            </div>
          </div>
        </section>

        {/* Photo credits. CC BY / CC BY-SA photos may only be shown with a
            visible credit: title, author, source, licence and whether we
            changed the image. The list is config/photoCredits.ts; the footer
            links here. `scroll-mt-28` keeps the heading clear of the sticky nav. */}
        <section
          id={PHOTO_CREDITS_ANCHOR}
          aria-labelledby="photo-credits-title"
          className={`scroll-mt-28 ${SECTION}`}
        >
          <div className="mx-auto max-w-5xl px-6">
            <h2
              id="photo-credits-title"
              tabIndex={-1}
              data-anchor-focus=""
              className={`${H2} ${textAlignClass} focus:outline-none`}
            >
              {t('about.credits.title')}
            </h2>
            <p className={`mt-3 max-w-3xl text-[1rem] leading-relaxed ${textAlignClass}`}>{t('about.credits.intro')}</p>
            <p className={`mt-1.5 max-w-3xl text-[0.875rem] ${textAlignClass}`}>{t('about.credits.editedNote')}</p>
            <ul role="list" className="m-0 mt-6 grid list-none gap-x-10 p-0 md:grid-cols-2">
              {PHOTO_CREDITS.map((photo) => (
                <li key={photo.subject} className="flex gap-4 border-b border-[var(--pv-border-subtle)] py-4">
                  {/* The photo itself, so the credit is easy to match to it. 4:3, 6rem wide. */}
                  <img
                    src={photo.src}
                    alt=""
                    width={photo.width}
                    height={photo.height}
                    loading="lazy"
                    decoding="async"
                    className="h-[4.5rem] w-24 shrink-0 rounded-lg bg-[var(--pv-color-surface-sunken)] object-cover"
                  />
                  <div className="min-w-0 flex-1 text-start">
                    <h3 className="text-[1rem] font-semibold text-[var(--pv-color-text)]">
                      {t(`about.credits.subjects.${photo.subject}`)}
                    </h3>
                    <p className="mt-0.5 text-[0.875rem]">{t(`about.credits.usedOn.${photo.usedOn}`)}</p>
                    <dl className="mt-2 space-y-1 text-[0.875rem] text-[var(--pv-color-text)]">
                      <div className="flex flex-wrap gap-x-1.5">
                        <dt className="font-semibold">{t('about.credits.by')}:</dt>
                        <dd className="min-w-0 break-words">
                          <bdi>{photo.author}</bdi>
                        </dd>
                      </div>
                      <div className="flex flex-wrap gap-x-1.5">
                        <dt className="font-semibold">{t('about.credits.source')}:</dt>
                        <dd className="min-w-0 break-words">
                          <a href={photo.sourceUrl} target="_blank" rel="noopener noreferrer" className={CREDIT_LINK}>
                            <bdi>{photo.title}</bdi>
                            <i className="ri-external-link-line ms-1 align-[-0.125em] text-base" aria-hidden="true" />
                            <span className="sr-only"> {t('about.credits.newTab')}</span>
                          </a>
                          <span className="text-[var(--pv-color-text-muted)]"> · {t('about.credits.sites.commons')}</span>
                        </dd>
                      </div>
                      <div className="flex flex-wrap gap-x-1.5">
                        <dt className="font-semibold">{t('about.credits.licence')}:</dt>
                        <dd className="min-w-0">
                          <a href={photo.licenceUrl} target="_blank" rel="noopener noreferrer license" className={CREDIT_LINK}>
                            <bdi>{photo.licence}</bdi>
                            <span className="sr-only"> {t('about.credits.newTab')}</span>
                          </a>
                          {photo.edited && (
                            <span className="text-[var(--pv-color-text-muted)]"> · {t('about.credits.edited')}</span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
